// src/components/useAnchoredPosition.ts
// Shared positioning for portal popovers: menus, dropdowns, pickers, tooltips.
//
// Every popover in the app used to carry its own copy of "read the trigger's
// rect, guess a side, clamp to the window". They disagreed on the details, and
// the ones that skipped the clamping ran off the screen edge. This is the one
// implementation: it measures the popover element itself, so it can flip to the
// side with room instead of hoping, and it keeps the result inside the viewport.
//
// The popover must be rendered with `position: fixed` in a portal — a popover
// left inside a scrolling ancestor gets clipped by that ancestor's overflow and
// counts towards its scrollHeight, which is how a menu ends up with a scrollbar
// of its own and drifts with the content behind it.

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

/** What the popover hangs off: an element, a viewport point, or a raw rect. */
export type Anchor = { x: number; y: number } | DOMRect | RefObject<HTMLElement | null>;

export interface AnchoredPositionOptions {
  /** Preferred side; flips to the opposite one when that side has no room. */
  side?: 'bottom' | 'top' | 'left' | 'right';
  /** Alignment along the other axis: the anchor's start edge, centre, or end. */
  align?: 'start' | 'center' | 'end';
  /** Distance between anchor and popover. */
  gap?: number;
  /** Minimum clearance from the viewport edges. */
  margin?: number;
  /** Never render narrower than the anchor — what a select's dropdown wants. */
  matchTriggerWidth?: boolean;
  /** Fixed width, when the caller knows it up front. */
  width?: number;
  /** Floor for the width; combines with matchTriggerWidth, whichever is wider. */
  minWidth?: number;
  /**
   * Cap on the popover's height. A long option list scrolling inside its own
   * box is fine; the viewport is always an additional cap.
   */
  maxHeight?: number;
}

export function isPointAnchor(anchor: Anchor): anchor is { x: number; y: number } {
  return 'x' in anchor && 'y' in anchor && !('width' in anchor);
}

function isRectAnchor(anchor: Anchor): anchor is DOMRect {
  return 'width' in anchor;
}

/** Viewport rect of the anchor, or null when the element isn't mounted yet. */
export function resolveAnchorRect(anchor: Anchor): DOMRect | null {
  if (isPointAnchor(anchor)) return new DOMRect(anchor.x, anchor.y, 0, 0);
  if (isRectAnchor(anchor)) return anchor;
  return anchor.current?.getBoundingClientRect() ?? null;
}

/**
 * Style for a fixed-position popover anchored to `anchor`.
 *
 * Returns null until the popover has been measured — render it hidden in that
 * first pass (see `hiddenUntilPositioned`) so it never flashes in the wrong
 * spot. Recomputed before paint, on scroll and resize, and whenever the
 * popover's own size changes.
 */
export function useAnchoredPosition(
  anchor: Anchor | null,
  open: boolean,
  ref: RefObject<HTMLElement | null>,
  options: AnchoredPositionOptions = {},
): CSSProperties | null {
  const {
    side = 'bottom',
    align = 'start',
    gap = 4,
    margin = 8,
    matchTriggerWidth = false,
    width,
    minWidth,
    maxHeight,
  } = options;

  const [style, setStyle] = useState<CSSProperties | null>(null);

  const updatePosition = useCallback(() => {
    const el = ref.current;
    const rect = anchor && resolveAnchorRect(anchor);
    if (!el || !rect) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = el.offsetWidth;
    const h = el.offsetHeight;

    let top: number;
    let left: number;

    if (side === 'bottom' || side === 'top') {
      const spaceBelow = vh - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;
      const wantsTop = side === 'top';
      const flip = wantsTop
        ? h > spaceAbove && spaceBelow > spaceAbove
        : h > spaceBelow && spaceAbove > spaceBelow;
      top = wantsTop !== flip ? rect.top - gap - h : rect.bottom + gap;
      left =
        align === 'end' ? rect.right - w
        : align === 'center' ? rect.left + rect.width / 2 - w / 2
        : rect.left;
    } else {
      const wantsLeft = side === 'left';
      const spaceRight = vw - rect.right - gap - margin;
      const spaceLeft = rect.left - gap - margin;
      const flip = wantsLeft
        ? w > spaceLeft && spaceRight > spaceLeft
        : w > spaceRight && spaceLeft > spaceRight;
      left = wantsLeft !== flip ? rect.left - gap - w : rect.right + gap;
      top =
        align === 'end' ? rect.bottom - h
        : align === 'center' ? rect.top + rect.height / 2 - h / 2
        : rect.top;
    }

    const viewportCap = vh - 2 * margin;
    const floor = Math.max(matchTriggerWidth ? rect.width : 0, minWidth ?? 0);

    setStyle({
      position: 'fixed',
      top: Math.max(margin, Math.min(top, vh - margin - h)),
      left: Math.max(margin, Math.min(left, vw - margin - w)),
      maxHeight: maxHeight ? Math.min(maxHeight, viewportCap) : viewportCap,
      ...(width !== undefined ? { width } : null),
      ...(floor > 0 ? { minWidth: floor } : null),
    });
  }, [anchor, ref, side, align, gap, margin, matchTriggerWidth, width, minWidth, maxHeight]);

  // Before paint, so the popover is never painted at a stale position.
  useLayoutEffect(() => {
    if (open) updatePosition();
    else setStyle(null);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const reposition = () => updatePosition();
    // `true` — a scroll in any ancestor moves the anchor, not just window scroll.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    // Content that grows after mount (async options, a filtered list) changes
    // which side fits.
    const el = ref.current;
    const observer = el ? new ResizeObserver(reposition) : null;
    if (el && observer) observer.observe(el);

    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      observer?.disconnect();
    };
  }, [open, ref, updatePosition]);

  return style;
}

/**
 * Style for the measuring pass: laid out so the popover has its natural size,
 * invisible so the user never sees it there.
 */
export const hiddenUntilPositioned: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  visibility: 'hidden',
};
